import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { recentSearches } from '../../../common/constants';
import './SearchBarComponent.scss';
import { ISuggestion } from '../../../common/view-model';

interface SearchBarPropsComponent {
	handleSearch: () => void;
	handleClose?: () => void;
	placeholder?: string;
	startSuggestionSearch: (term: string) => void;
	value?: string;
	reset: () => void;
	handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	handleClearSearch: () => void;
	selectedSuggestionIndex: number;
	setSelectedSuggestionIndex: React.Dispatch<React.SetStateAction<number>>;
	suggestions: ISuggestion[];
	showSuggestions?: boolean;
	handleDismissSuggestions?: () => void;
	onSuggestionSelected: (suggestion: string, type: string) => void;
	setValueCallback: (value: string) => void;
}

export const SearchBarComponent: FC<SearchBarPropsComponent> = ({
	handleSearch,
	placeholder,
	handleClose,
	value,
	reset,
	handleChange,
	handleClearSearch,
	selectedSuggestionIndex,
	setSelectedSuggestionIndex,
	suggestions,
	showSuggestions,
	handleDismissSuggestions,
	onSuggestionSelected,
	setValueCallback
}) => {
	const [recent, setRecent] = useState<string[]>([]);
	const [showRecent, setShowRecent] = useState(false);
	const [selectedRecentIndex, setSelectedRecentIndex] = useState(-1);
	const [disableHover, setDisableHover] = useState(false);
	const hasSearchTerm = Boolean(value?.trim());
	const visibleSuggestionCount =
		hasSearchTerm && showSuggestions !== false ? suggestions.length : 0;
	const defaultPlaceholder = 'Search for products, brands, ingredients and more';
	const inputRef = useRef<HTMLInputElement>(null);
	const dismissDropdowns = useCallback(() => {
		setShowRecent(false);
		setSelectedRecentIndex(-1);
		setSelectedSuggestionIndex(-1);
		handleDismissSuggestions?.();
		inputRef.current?.blur();
	}, [handleDismissSuggestions, setSelectedSuggestionIndex]);

	useEffect(() => {
		const storedRecentSearches = JSON.parse(localStorage.getItem(recentSearches) || '[]');
		setRecent(storedRecentSearches);
	}, []);

	useEffect(() => {
		const loadRecent = () => {
			const stored = JSON.parse(localStorage.getItem(recentSearches) || '[]');
			setRecent(stored);
		};
		loadRecent();
		window.addEventListener('recentSearchUpdated', loadRecent);
		return () => {
			window.removeEventListener('recentSearchUpdated', loadRecent);
		};
	}, []);

	useEffect(() => {
		const handleOutsideClick = (event: MouseEvent) => {
			const target = event.target;
			const searchContainer = inputRef.current?.closest('.search-container');

			if (target instanceof Node && searchContainer?.contains(target)) return;

			dismissDropdowns();
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [dismissDropdowns]);

	const closeIconCick = useCallback(() => {
		reset();
		handleClose && handleClose();
		handleClearSearch();
	}, [handleClearSearch, handleClose, reset]);
	const postSearchItem = useCallback(
		(term: string) => {
			const recentSearchData = [term, ...recent.filter(r => r !== term)].slice(0, 6);
			localStorage.setItem(recentSearches, JSON.stringify(recentSearchData));
			setRecent(recentSearchData);
		},
		[recent]
	);
	const handleRecentClick = useCallback(
		(term: string) => {
			postSearchItem(term);
			onSuggestionSelected(term, '');
			setValueCallback(term);
			dismissDropdowns();
		},
		[dismissDropdowns, onSuggestionSelected, postSearchItem, setValueCallback]
	);
	const moveSelection = useCallback(
		(direction: 1 | -1) => {
			const visibleRecentCount = showRecent ? recent.length : 0;
			const totalCount = visibleSuggestionCount + visibleRecentCount;
			if (totalCount === 0) return;

			let currentIndex = -1;
			if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < visibleSuggestionCount) {
				currentIndex = selectedSuggestionIndex;
			} else if (selectedRecentIndex >= 0 && selectedRecentIndex < visibleRecentCount) {
				currentIndex = visibleSuggestionCount + selectedRecentIndex;
			}

			let nextIndex: number;
			if (direction === 1) {
				nextIndex = (currentIndex + 1) % totalCount;
			} else if (currentIndex <= 0) {
				nextIndex = totalCount - 1;
			} else {
				nextIndex = currentIndex - 1;
			}

			if (nextIndex < visibleSuggestionCount) {
				setSelectedSuggestionIndex(nextIndex);
				setSelectedRecentIndex(-1);
			} else {
				setSelectedSuggestionIndex(-1);
				setSelectedRecentIndex(nextIndex - visibleSuggestionCount);
			}
		},
		[
			recent.length,
			selectedRecentIndex,
			selectedSuggestionIndex,
			setSelectedSuggestionIndex,
			showRecent,
			visibleSuggestionCount
		]
	);
	// const handleKeyUp = useCallback(
	// 	(event: React.KeyboardEvent<HTMLInputElement>) => {
	// 		event.preventDefault();
	// 		if (event.key === 'Enter' && event.currentTarget.value.trim()) {
	// 			if (inputRef.current) {
	// 				inputRef.current?.blur();
	// 				setDisableHover(true);
	// 			}
	// 			postSearchItem(event.currentTarget.value.trim());
	// 			handleSearch();
	// 			setShowRecent(false);
	// 		}
	// 	},
	// 	[handleSearch]
	// );
	const handleArrowNavigation = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return false;

			if (visibleSuggestionCount || (showRecent && recent.length)) {
				event.preventDefault();
				const direction = event.key === 'ArrowDown' ? 1 : -1;
				moveSelection(direction);
			}

			return true;
		},
		[moveSelection, recent.length, showRecent, visibleSuggestionCount]
	);
	const handleEscapeKey = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key !== 'Escape') return false;

			event.preventDefault();
			dismissDropdowns();
			return true;
		},
		[dismissDropdowns]
	);
	const handleEnterKey = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key !== 'Enter') return false;

			if (selectedRecentIndex >= 0) {
				event.preventDefault();
				const term = recent[selectedRecentIndex];

				if (term) {
					handleRecentClick(term);
				}

				return true;
			}

			if (hasSearchTerm && selectedSuggestionIndex >= 0) {
				event.preventDefault();
				const suggestion = suggestions[selectedSuggestionIndex];

				if (suggestion) {
					postSearchItem(suggestion.name);
					window.dispatchEvent(new Event('recentSearchUpdated'));
					onSuggestionSelected(suggestion.name, suggestion.type);
					setValueCallback(suggestion.name);
					dismissDropdowns();
				}

				return true;
			}

			const term = event.currentTarget.value.trim();
			if (!term) return false;

			event.preventDefault();
			if (inputRef.current) {
				inputRef.current.blur();
				setDisableHover(true);
			}

			postSearchItem(term);
			handleSearch();
			setShowRecent(false);
			return true;
		},
		[
			dismissDropdowns,
			handleRecentClick,
			handleSearch,
			hasSearchTerm,
			onSuggestionSelected,
			postSearchItem,
			recent,
			selectedRecentIndex,
			selectedSuggestionIndex,
			setSelectedSuggestionIndex,
			setValueCallback,
			suggestions
		]
	);
	const handleKeyUp = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (handleArrowNavigation(event)) return;
			if (handleEscapeKey(event)) return;
			handleEnterKey(event);
		},
		[handleArrowNavigation, handleEnterKey, handleEscapeKey]
	);

	const clearRecentSeraches = () => {
		localStorage.removeItem(recentSearches);
		setRecent([]);
	};

	return (
		<div className='search-bar-container'>
			<input
				ref={inputRef}
				type='text'
				className={`search-bar ${value ? 'typing' : ''} ${disableHover ? 'disable-hover' : ''}`}
				value={value}
				onChange={event => {
					setSelectedSuggestionIndex(-1);
					setSelectedRecentIndex(-1);
					handleChange?.(event);
				}}
				placeholder={placeholder || defaultPlaceholder}
				onKeyUp={handleKeyUp}
				onFocus={() => {
					setShowRecent(true);
					setSelectedRecentIndex(-1);
					setDisableHover(false);
				}}
				onBlur={() => {
					setShowRecent(false);
					setSelectedRecentIndex(-1);
				}}
			/>
			{showRecent && recent.length > 0 && (
				<div className='recent-dropdown'>
					<div className='recent-header'>
						<span>Recent Searches</span>
					</div>
					<div className='recent-item-list'>
						<ul className='recent-list' role='listbox' aria-label='Recent searches'>
							{recent.map((r, i) => (
								<li
									key={i}
									role='option'
									aria-selected={selectedRecentIndex === i}
									className={`recent-item ${selectedRecentIndex === i ? 'selected' : ''}`}
									onMouseDown={e => {
										e.preventDefault();
										handleRecentClick(r);
									}}
								>
									{r}
								</li>
							))}
						</ul>
					</div>
					<div
						className='clear-recent'
						onMouseDown={e => {
							e.preventDefault();
							clearRecentSeraches();
						}}
					>
						Clear Recent Searches
					</div>
				</div>
			)}
			<div className={`icons-container ${!value ? 'empty-wrapper' : ''}`}>
				{value && <i className='ri-close-line clean-icon' onClick={closeIconCick}></i>}
				<i
					className='ri-search-line search-icon'
					onClick={() => {
						if (value) {
							postSearchItem(value);
						}
						handleSearch();
					}}
				></i>
			</div>
		</div>
	);
};

// import { FC, useCallback, useEffect, useRef, useState } from 'react';
// import { recentSearches } from '../../../common/constants';
// import './SearchBarComponent.scss';
// import {
// 	getIirisCookie,
// 	getLocalStorageValue,
// 	IirisIdentityKey
// } from '../../../helpers/authenticationHelper';

// interface SearchBarPropsComponent {
// 	handleSearch: () => void;
// 	handleClose?: () => void;
// 	placeholder?: string;
// 	startSuggestionSearch: (term: string) => void;
// 	value?: string;
// 	reset: () => void;
// 	handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
// 	handleClearSearch: () => void;
// }

// export const SearchBarComponent: FC<SearchBarPropsComponent> = ({
// 	handleSearch,
// 	placeholder,
// 	handleClose,
// 	value,
// 	reset,
// 	handleChange,
// 	handleClearSearch
// }) => {
// 	const [recent, setRecent] = useState<string[]>([]);
// 	const [showRecent, setShowRecent] = useState(false);
// 	const [disableHover, setDisableHover] = useState(false);
// 	const defaultPlaceholder = 'Search for products, brands, ingredients and more';
// 	const inputRef = useRef<HTMLInputElement>(null);

// 	const emailId = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
// 	const token = getIirisCookie();

// 	useEffect(() => {
// 		const storedRecentSearches = JSON.parse(localStorage.getItem(recentSearches) || '[]');
// 		setRecent(storedRecentSearches);
// 	}, []);

// 	useEffect(() => {
// 		const loadRecent = () => {
// 			const stored = JSON.parse(localStorage.getItem(recentSearches) || '[]');
// 			setRecent(stored);
// 		};
// 		loadRecent();
// 		window.addEventListener('recentSearchUpdated', loadRecent);
// 		return () => {
// 			window.removeEventListener('recentSearchUpdated', loadRecent);
// 		};
// 	}, []);

// 	const closeIconCick = useCallback(() => {
// 		reset();
// 		handleClose && handleClose();
// 		handleClearSearch();
// 	}, [handleClearSearch, handleClose, reset]);
// 	const handleKeyUp = useCallback(
// 		(event: React.KeyboardEvent<HTMLInputElement>) => {
// 			event.preventDefault();
// 			if (event.key === 'Enter' && event.currentTarget.value.trim()) {
// 				if (inputRef.current) {
// 					inputRef.current?.blur();
// 					setDisableHover(true);
// 				}
// 				postSearchItem(event.currentTarget.value.trim());
// 				handleSearch();
// 				setShowRecent(false);
// 			}
// 		},
// 		[handleSearch]
// 	);

// 	const handleRecentClick = useCallback(
// 		(term: string) => {
// 			if (handleChange) {
// 				handleChange({ target: { value: term } } as React.ChangeEvent<HTMLInputElement>);
// 			}
// 			postSearchItem(term);
// 			setShowRecent(false);
// 		},
// 		[handleSearch]
// 	);

// 	const postSearchItem = (term: any) => {
// 		const recentSearchData = [term, ...recent.filter(r => r !== term)].slice(0, 6);
// 		localStorage.setItem(recentSearches, JSON.stringify(recentSearchData));
// 		setRecent(recentSearchData);
// 	};

// 	const clearRecentSeraches = () => {
// 		localStorage.removeItem(recentSearches);
// 		setRecent([]);
// 	};

// 	const getCognitoMailId = async () => {
// 		try {
// 			const response = await fetch(
// 				`${
// 					import.meta.env.VITE_IRIS_DOMAIN
// 				}/v3/authorization/public/passport/userdetails?client_id=${
// 					import.meta.env.VITE_CLIENT_ID
// 				}&username=${emailId}`
// 			);
// 			if (!response.ok) {
// 				throw new Error('Failed to fetch recent searches');
// 			}
// 			const data = await response.json();
// 			getRecentSearches(data?.data?.Username);
// 			return data;
// 		} catch (error) {
// 			console.error('Error fetching recent searches:', error);
// 		}
// 	};

// 	const getRecentSearches = async (cognitoUserId: string) => {
// 		try {
// 			const response = await fetch(
// 				`${import.meta.env.VITE_BEACON_API_URL}/search/${cognitoUserId}`,
// 				{ headers: { Authorization: token || '', email: emailId || '' } }
// 			);
// 			if (!response.ok) {
// 				throw new Error('Failed to fetch recent searches');
// 			}
// 			const data = await response.json();
// 			const recentTerms =
// 				data?.recentSearches?.map((item: { term: string }) => item.term).slice(0, 6) || [];
// 			setRecent(recentTerms);
// 			return data;
// 		} catch (error) {
// 			console.error('Error fetching recent searches:', error);
// 		}
// 	};

// 	useEffect(() => {
// 		getCognitoMailId();
// 	}, []);

// 	return (
// 		<div className='search-bar-container'>
// 			<input
// 				ref={inputRef}
// 				type='text'
// 				className={`search-bar ${value ? 'typing' : ''} ${disableHover ? 'disable-hover' : ''}`}
// 				value={value}
// 				onChange={handleChange}
// 				placeholder={placeholder || defaultPlaceholder}
// 				onKeyUp={handleKeyUp}
// 				onFocus={() => {
// 					setShowRecent(true);
// 					setDisableHover(false);
// 				}}
// 				onBlur={() => setShowRecent(false)}
// 			/>
// 			{showRecent && recent.length > 0 && (
// 				<div className='recent-dropdown'>
// 					<div className='recent-header'>
// 						<span>Recent Searches</span>
// 					</div>
// 					<div className='recent-item-list'>
// 						<ul className='recent-list'>
// 							{recent.map((r, i) => (
// 								<li
// 									key={i}
// 									className='recent-item'
// 									onMouseDown={e => {
// 										e.preventDefault();
// 										handleRecentClick(r);
// 									}}
// 								>
// 									{r}
// 								</li>
// 							))}
// 						</ul>
// 					</div>
// 					<div
// 						className='clear-recent'
// 						onMouseDown={e => {
// 							e.preventDefault();
// 							clearRecentSeraches();
// 						}}
// 					>
// 						Clear Recent Searches
// 					</div>
// 				</div>
// 			)}
// 			<div className={`icons-container ${!value ? 'empty-wrapper' : ''}`}>
// 				{value && <i className='ri-close-line clean-icon' onClick={closeIconCick}></i>}
// 				<i
// 					className='ri-search-line search-icon'
// 					onClick={() => {
// 						postSearchItem(value);
// 						handleSearch();
// 					}}
// 				></i>
// 			</div>
// 		</div>
// 	);
// };
